import React, { useLayoutEffect, useRef } from "react";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle,
  Clock,
  CreditCard,
  Phone,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Truck,
  XCircle,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../contexts/ThemeContext";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "#2B0000";
const MAROON_DARK = "#190000";
const GOLD = "#D6B33F";
const GOLD_LIGHT = "#E9CD7A";
const CREAM = "#FBF6EF";
const SOFT_TEXT = "#6F625E";

const policySections = [
  {
    icon: CalendarDays,
    title: "1. Eligibility for Returns & Exchanges",
    details: [
      "Products must be returned/exchanged within 7 days of delivery.",
      "Items must be unused, unworn, and in their original packaging with all tags and certificates (if any).",
      "Customized, engraved, or personalized products are not eligible for return or exchange unless defective or damaged.",
      "Sale or discounted items may not be eligible for return/exchange.",
    ],
  },
  {
    icon: CheckCircle,
    title: "2. Conditions for Return",
    details: [
      "The product must be in resalable condition without any damage.",
      "A valid purchase invoice or order confirmation is required.",
      "Any missing accessories, certificates, or packaging may result in rejection of return.",
    ],
  },
  {
    icon: RotateCcw,
    title: "3. Exchange Policy",
    details: [
      "Customers can exchange eligible products within 7 days of purchase.",
      "If the exchanged product value is higher, the customer must pay the difference.",
      "If the value is lower, the balance may be provided as a store credit (no cash refunds unless specified).",
    ],
  },
  {
    icon: CreditCard,
    title: "4. Refund Policy",
    details: [
      "Refunds (if applicable) will be initiated once the returned product passes quality inspection.",
      "Refunds will be processed to the original mode of payment within 7-10 business days.",
      "Shipping charges, if any, are non-refundable.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "5. Damaged or Defective Products",
    details: [
      "If you receive a defective or damaged product, please notify us within 48 hours of delivery.",
      "We will arrange for return pickup or replacement at no extra cost.",
    ],
  },
  {
    icon: XCircle,
    title: "6. Non-Returnable Items",
    details: [
      "Customized or made-to-order jewelry.",
      "Items without original invoice/certificates.",
      "Products damaged due to customer mishandling.",
    ],
  },
  {
    icon: Truck,
    title: "7. Process for Return/Exchange",
    details: [
      "Contact our Customer Support at slgoldkanthi@info.com / +91-86400-51400.",
      "Provide order details and reason for return/exchange.",
      "Our team will arrange pickup or guide you through the return process.",
      "After inspection, refund or exchange will be processed.",
    ],
  },
];

export default function ReturnExchangePolicy() {
  const { theme } = useTheme();

  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const headingRef = useRef(null);
  const sectionsRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            heroRef.current,
            headingRef.current,
            sectionsRef.current,
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
          }
        );
        return;
      }

      // Hero animation
      gsap.fromTo(
        heroRef.current,
        {
          opacity: 0,
          y: 35,
          scale: 0.985,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.05,
          ease: "power3.out",
        }
      );

      // Heading animation
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          x: -45,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          delay: 0.15,
          ease: "power3.out",
        }
      );

      // Sections animation
      const sectionElements = gsap.utils.toArray(".policy-section");
      gsap.fromTo(
        sectionElements,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Detail items animation
      const detailItems = gsap.utils.toArray(".policy-detail-item");
      gsap.fromTo(
        detailItems,
        {
          opacity: 0,
          x: 20,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.05,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Parallax effect for hero image
      gsap.to(".policy-hero-image", {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main
      ref={pageRef}
      className="min-h-screen overflow-hidden"
      style={{
        backgroundColor: theme?.user_bg || CREAM,
        color: theme?.user_text || MAROON_DARK,
      }}
    >
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="
          relative min-h-[380px] overflow-hidden
          border-b border-[#D6B33F]/20
          sm:min-h-[400px]
          lg:min-h-[300px]
        "
      >
        <div className="absolute inset-0">
          <img
            className="
              policy-hero-image h-full w-full scale-[1.08] object-cover
              object-[55%_center]
              lg:scale-105 lg:object-center
            "
            src="https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=90&w=1800&auto=format&fit=crop"
            alt="Return and exchange policy"
          />
          <div
            className="
              absolute inset-0
              bg-gradient-to-b
              from-[#FBF6EF]/95 via-[#FBF6EF]/65 to-[#FBF6EF]/95
              lg:bg-gradient-to-r
              lg:from-[#FBF6EF] lg:via-[#FBF6EF]/40 lg:to-transparent
            "
          />
          <div className="absolute inset-0 bg-[#2B0000]/10" />
        </div>

        <div
          className="
            relative z-10 mx-auto flex min-h-[380px] max-w-[1420px]
            items-center px-4 pb-8 pt-12
            sm:min-h-[400px] sm:px-6 sm:pt-14
            lg:min-h-[300px] lg:px-14 lg:py-14
            xl:px-20
          "
        >
          <div
            ref={headingRef}
            className="
              w-full max-w-[620px] rounded-[24px]
              border border-white/60 bg-[#FBF6EF]/80
              px-4 py-5 shadow-[0_24px_70px_rgba(43,0,0,0.08)]
              backdrop-blur-[5px]
              sm:px-6 sm:py-7
              lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none
              lg:backdrop-blur-none
            "
          >
            <div
              className="
                mb-3 inline-flex items-center gap-2 rounded-full
                border border-[#D6B33F]/35 bg-white/65
                px-2.5 py-1 text-[9px] font-semibold uppercase
                tracking-[0.28em] sm:text-[10px] sm:px-3 sm:py-1.5
              "
              style={{ color: MAROON }}
            >
              <Sparkles size={13} style={{ color: GOLD }} />
              Customer Satisfaction First
            </div>

            <h1
              className="
                font-serif text-[32px] leading-[1.05]
                sm:text-[42px] lg:text-[58px]
              "
              style={{ color: MAROON_DARK }}
            >
              Return & Exchange Policy
            </h1>

            <div className="mt-4 flex items-center gap-3">
              <span className="h-px w-12" style={{ backgroundColor: GOLD }} />
              <span
                className="
                  flex h-6 w-6 rotate-45 items-center justify-center
                  rounded-[6px] border
                  sm:h-7 sm:w-7
                "
                style={{
                  borderColor: `${GOLD}80`,
                  backgroundColor: "#FFFDF9",
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-[2px] sm:h-2 sm:w-2"
                  style={{ backgroundColor: GOLD }}
                />
              </span>
              <span className="h-px w-12" style={{ backgroundColor: GOLD }} />
            </div>

            <p
              className="
                mt-4 text-sm leading-6
                sm:text-[15px] sm:leading-7
                lg:max-w-[540px]
              "
              style={{ color: SOFT_TEXT }}
            >
              At SL Gold Kanthi, customer satisfaction is our priority. We ensure
              that every product is crafted with care and quality.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative px-4 py-10 sm:px-6 lg:px-14 lg:py-16 xl:px-20">
        <div className="pointer-events-none absolute left-0 top-0 h-72 w-72 rounded-full bg-[#D6B33F]/[0.05] blur-3xl" />

        <div
          className="relative mx-auto max-w-[1420px]"
          ref={sectionsRef}
        >
          {/* Policy Sections */}
          <div className="space-y-6 sm:space-y-8">
            {policySections.map(({ icon: Icon, title, details }) => (
              <div
                key={title}
                id={title.toLowerCase().replace(/\s+/g, "-")}
                className="
                  policy-section scroll-mt-24 rounded-[20px]
                  border bg-white/55 p-5 shadow-[0_12px_35px_rgba(43,0,0,0.04)]
                  backdrop-blur-sm transition-all duration-300
                  hover:shadow-[0_18px_45px_rgba(43,0,0,0.08)]
                  sm:p-6
                  lg:p-8
                "
                style={{
                  borderColor: theme?.user_border || "#E8D9D0",
                }}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="
                      flex h-10 w-10 shrink-0 items-center justify-center
                      rounded-full
                      sm:h-12 sm:w-12
                    "
                    style={{
                      backgroundColor: `${GOLD}15`,
                      color: MAROON,
                    }}
                  >
                    <Icon size={18} strokeWidth={1.8} className="sm:w-[22px] sm:h-[22px]" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2
                      className="font-serif text-xl font-semibold sm:text-2xl lg:text-3xl"
                      style={{ color: MAROON_DARK }}
                    >
                      {title}
                    </h2>

                    <ul className="mt-3 space-y-2 sm:mt-4 sm:space-y-2.5">
                      {details.map((detail) => (
                        <li
                          key={detail}
                          className="policy-detail-item flex items-start gap-2.5 text-sm sm:text-[15px]"
                          style={{ color: theme?.user_text || MAROON_DARK }}
                        >
                          <CheckCircle
                            size={16}
                            className="mt-0.5 shrink-0 sm:w-[18px] sm:h-[18px]"
                            style={{ color: GOLD }}
                          />
                          <span className="break-words">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div
              className="
                mt-6 rounded-[20px] border
                bg-gradient-to-br from-[#2B0000] to-[#190000]
                p-5 text-white
                sm:p-6
                lg:p-8
              "
              style={{
                borderColor: `${GOLD}30`,
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div
                  className="
                    flex h-12 w-12 shrink-0 items-center justify-center
                    rounded-full border
                  "
                  style={{
                    borderColor: `${GOLD}50`,
                  }}
                >
                  <Phone size={20} style={{ color: GOLD_LIGHT }} />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg font-semibold sm:text-xl">
                    8. Contact Us
                  </h3>
                  <p
                    className="mt-1 text-sm text-white/70"
                  >
                    For any questions about our Return & Exchange Policy, reach us at:
                  </p>

                  <div className="mt-4 space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/50 sm:text-xs">
                        Email
                      </p>
                      <a
                        href="mailto:slgoldkanthi@info.com"
                        className="text-sm hover:underline break-words"
                        style={{ color: GOLD_LIGHT }}
                      >
                        slgoldkanthi@info.com
                      </a>
                    </div>

                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/50 sm:text-xs">
                        Phone
                      </p>
                      <a
                        href="tel:+918640051400"
                        className="text-sm hover:underline"
                        style={{ color: GOLD_LIGHT }}
                      >
                        +91-86400-51400
                      </a>
                    </div>

                    <div className="sm:col-span-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-white/50 sm:text-xs">
                        Address
                      </p>
                      <p className="text-sm text-white/80 break-words">
                        Building No.1149, Shop No.5, 1st floor, Kucha
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <a
                      href="mailto:slgoldkanthi@info.com"
                      className="
                        group flex items-center gap-2 rounded-full
                        px-4 py-2 text-sm font-medium transition-all
                        duration-300 hover:translate-x-1
                        sm:px-5 sm:py-2.5
                      "
                      style={{
                        backgroundColor: GOLD,
                        color: MAROON_DARK,
                      }}
                    >
                      Email Us
                      <ArrowRight size={15} className="sm:w-[16px] sm:h-[16px]" />
                    </a>
                    <a
                      href="tel:+918640051400"
                      className="
                        group flex items-center gap-2 rounded-full
                        border px-4 py-2 text-sm font-medium
                        transition-all duration-300 hover:translate-x-1
                        sm:px-5 sm:py-2.5
                      "
                      style={{
                        borderColor: `${GOLD}50`,
                        color: GOLD_LIGHT,
                      }}
                    >
                      Call Us
                      <ArrowRight size={15} className="sm:w-[16px] sm:h-[16px]" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}